import { ErrorHandler, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule } from "@angular/router";
import { CoreCommonModule } from "./utils/modules/core/modules/core-common.module";
import { AppComponent } from "./app.component";
import { WtfMainModule } from "./components/main/main.module";
import { WtfSplashScreenService } from "./services/core/splash-screen.service";
import { WtfConfigService } from "./services/core/config.service";
import { WtfNavigationService } from "./components/navigation/navigation.service";
import {
    TranslateModule,
    TranslateLoader,
    MissingTranslationHandler,
} from "@ngx-translate/core";
import { JwtModule, JwtModuleOptions } from "@auth0/angular-jwt";
import { PdfViewerModule } from "ng2-pdf-viewer";
import { CustomErrorHandler } from "./services/core/custom-error-handler.service";
import { environment as env } from "../environments/environment";
import { ROUTES } from "./app.routes";
import { ObservableDataService } from "./services/core/observable-data.service";
import { CoreMapper } from "./utils/mappers/core/core.mapper";
import { FileService } from "./services/shared/file.service";
import { SharedModule } from "./components/shared/shared.module";
import { CdkTableModule } from "@angular/cdk/table";
import { MatTableModule } from "@angular/material/table";
import { LoggedUserGuard } from "./utils/guards/core/logged-user.guard";

import { UnLoggedUserGuard } from "./utils/guards/core/unlogged-user.guard";

import { TagInputModule } from "ngx-chips";

import { GrupoFormComponent } from "./components/mediaManager/grupos/components/grupo-form/grupo-form.component";
import { MiembroFormComponent } from "./components/mediaManager/grupos/components/miembro-form/miembro-form.component";
import { SmsFormComponent } from "./components/mediaManager/campanias/sms/components/sms-form/sms-form.component";
import { SatFormComponent } from "./components/mediaManager/campanias/sat/components/sat-form/sat-form.component";
import { SmsFormCSVComponent } from "./components/mediaManager/campanias/sms/components/sms-form-csv/sms-form-csv.component";
import { MiembroFormCSVComponent } from "./components/mediaManager/grupos/components/miembro-form-csv/miembro-form-csv.component";
import { SmsProgramadoFormComponent } from "./components/mediaManager/campanias/sms/components/sms-programado-form/sms-programado-form.component";
import { SatProgramadoFormComponent } from "./components/mediaManager/campanias/sat/components/sat-programado-form/sat-programado-form.component";

import { NgxChartsModule } from "@swimlane/ngx-charts";
import { AuthenticationService } from "./services/security/authentication.service";
import { SecurityMapper } from "./utils/mappers/security/security.mapper";
import { Error500Module } from "./components/errors/500/error-500.module";

import { UserFormComponent } from "./components/mediaManager/admon/company/components/company-details/tabs/user-list/user-form/user-form.component";
import { CompanyFormComponent } from "./components/mediaManager/admon/company/components/company-form/company-form.component";
import { ContactFormComponent } from "./components/mediaManager/admon/company/components/company-details/tabs/contact-list/contact-form/contact-form.component";
import { AppFormComponent } from "./components/mediaManager/admon/company/components/company-details/tabs/app-list/app-form/app-form.component";
import { CountryFormComponent } from "./components/mediaManager/admon/country/components/country-form/country-form.component";
import { ProtocolFormComponent } from "./components/mediaManager/admon/protocol/components/protocol-form/protocol-form.component";
import { OperatorFormComponent } from "./components/mediaManager/admon/operator/components/operator-form/operator-form.component";
import { SatDetailReportComponent } from "./components/mediaManager/campanias/sat/components/sat-detail-report/sat-detail-report.component";
import { ShortCodeFormComponent } from "./components/mediaManager/admon/operator/components/operator-detail/tabs/shortcode-list/components/shortcode-form/shortcode-form.component";
import { ContactRuleFormComponent } from "./components/mediaManager/admon/company/components/company-details/tabs/contact-rules/contact-rule-form/contact-rule-form.component";
import { SatErrorReportComponent } from "./components/mediaManager/reports/sat/components/error/sat-error-report.component";
import { PeriodContactabilityFormComponent } from "./components/mediaManager/admon/period-contactability/components/period-contactability-form/period-contactability-form.component";
import { OperatorCompanyFormComponent } from "./components/mediaManager/admon/company/components/company-details/tabs/operator-list/operator-form/operator-form.component";
import { ModulePermissionGuard } from "./utils/guards/core/module-permission.guard";
import { FileUploadModule } from "ng2-file-upload";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgScrollbarModule } from "ngx-scrollbar";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { ChartModule } from "primeng/chart";
import { PdfJsViewerModule } from "ng2-pdfjs-viewer";
import { EmailEditorModule } from "@trippete/angular-email-editor";
import { CustomFormsModule } from "@rizwan3d/ngx-custom-validators-16";
import { NgxMatTimepickerModule } from "ngx-mat-timepicker";
import { UploadFileHistoryComponent } from "./components/mediaManager/grupos/components/upload-file-history/upload-file-history.component";
import { ApprovalWorkflowFormComponent } from "./components/mediaManager/admon/approve-workflow/components/approval-workflow-form/approval-workflow-form.component";
import { ResizeObserverModule } from "@ng-web-apis/resize-observer";
import { MissingTranslationHelper } from "./utils/handlers/translate.handler";
import { SmsErrorReportComponent } from "./components/mediaManager/reports/sms/components/web-sms-report/components/sms/error/sms-error-report.component";

export function gettoken() {
    const info = localStorage.getItem(env.accessTokenLocalStorageKey);

    if (info) {
        return JSON.parse(info).token;
    }
}

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

const jwtConf: JwtModuleOptions = {
    config: {
        tokenGetter: gettoken,
        allowedDomains: [env.ApiUrl],
    },
};

@NgModule({
    declarations: [
        AppComponent,
        GrupoFormComponent,
        CompanyFormComponent,
        CountryFormComponent,
        ContactFormComponent,
        AppFormComponent,
        ApprovalWorkflowFormComponent,
        MiembroFormComponent,
        UploadFileHistoryComponent,
        UserFormComponent,
        ProtocolFormComponent,
        OperatorFormComponent,
        OperatorCompanyFormComponent,
        PeriodContactabilityFormComponent,
        SmsFormComponent,
        SmsProgramadoFormComponent,
        SmsFormCSVComponent,
        MiembroFormCSVComponent,
        SatFormComponent,
        SatProgramadoFormComponent,
        SatDetailReportComponent,
        ShortCodeFormComponent,
        ContactRuleFormComponent,
        SatErrorReportComponent,
        SmsErrorReportComponent,
    ],
    imports: [
        BrowserAnimationsModule,
        ResizeObserverModule,
        BrowserModule,
        EmailEditorModule,
        // HttpModule,
        // this line you need
        NgxChartsModule,
        HttpClientModule,
        NgxMatTimepickerModule,
        BrowserAnimationsModule,
        MatTableModule,
        CdkTableModule,
        CoreCommonModule,
        SharedModule,
        PdfViewerModule,
        PdfJsViewerModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        WtfMainModule,
        // Errors
        Error500Module,
        TagInputModule,
        CustomFormsModule,
        // MatTimepickerModule,
        FileUploadModule,
        RouterModule.forRoot(ROUTES, {
            useHash: true,
        }),
        JwtModule.forRoot(jwtConf),
        FormsModule,
        ReactiveFormsModule,
        NgScrollbarModule,
        ChartModule,
    ],
    providers: [
        WtfSplashScreenService,
        WtfConfigService,
        WtfNavigationService,
        ObservableDataService,
        CoreMapper,
        FileService,
        LoggedUserGuard,
        AuthenticationService,
        UnLoggedUserGuard,
        SecurityMapper,
        {
            provide: ErrorHandler,
            useClass: CustomErrorHandler,
        },
        ModulePermissionGuard,
        //  { provide: RequestOptions, useClass: CustomRequestOptions }
    ],
    bootstrap: [AppComponent],
    exports: [],
})
export class AppModule {}
